import { dspAssetUrl } from './dspAssetUrl';
/**
 * The one compiled CookUp DSP WebAssembly module.
 *
 * Every worklet (sampler, physical instruments, AFM, delay, reverb, LA-2A)
 * instantiates the same binary. Each factory used to fetch and compile its own
 * copy, which cost five downloads and five compiles on session start; this
 * compiles once and hands the same WebAssembly.Module to every processor.
 *
 * Tests have no network or WASM bytes; they return null unless a test
 * installs a module via globalThis.__COOKUP_DSP_MODULE__.
 */

export const DSP_WASM_URL = dspAssetUrl('/audio/cookup_audio_dsp.wasm');

let dspModuleLoad = null;

export function loadDspModule() {
  if (globalThis.__COOKUP_DSP_MODULE__ !== undefined) return Promise.resolve(globalThis.__COOKUP_DSP_MODULE__);
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') return Promise.resolve(null);
  if (typeof globalThis.fetch !== 'function' || typeof WebAssembly !== 'object') return Promise.resolve(null);
  if (!dspModuleLoad) {
    dspModuleLoad = globalThis.fetch(DSP_WASM_URL)
      .then((response) => {
        if (!response.ok) throw new Error(`CookUp DSP fetch failed (${response.status})`);
        return typeof WebAssembly.compileStreaming === 'function'
          && String(response.headers?.get?.('content-type') || '').includes('application/wasm')
          ? WebAssembly.compileStreaming(response)
          : response.arrayBuffer().then((bytes) => WebAssembly.compile(bytes));
      })
      .catch(() => {
        // A transient network or deployment error must not poison later loads.
        dspModuleLoad = null;
        return null;
      });
  }
  return dspModuleLoad;
}

/**
 * Build a stereo-in/stereo-out FX worklet node backed by the Rust DSP.
 *
 * Returns null when the worklet or the WASM module is unavailable — there is
 * no JavaScript DSP fallback in the processors. Callers build their native
 * Web Audio equivalent instead, so the effect is never silently missing.
 */
export async function createDspFxNode(context, { workletUrl, processorName, params, workletLoads }) {
  if (!context?.audioWorklet || typeof globalThis.AudioWorkletNode !== 'function') return null;
  let workletLoad = workletLoads.get(context);
  if (!workletLoad) {
    workletLoad = context.audioWorklet.addModule(workletUrl);
    workletLoads.set(context, workletLoad);
  }
  try {
    const [, dspModule] = await Promise.all([workletLoad, loadDspModule()]);
    if (!dspModule) return null;
    return new globalThis.AudioWorkletNode(context, processorName, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 2,
      channelCountMode: 'max',
      processorOptions: { params, dspModule }
    });
  } catch (error) {
    // Includes engines that cannot structured-clone a WebAssembly.Module.
    workletLoads.delete(context);
    return null;
  }
}
